import { Component, Input, OnInit } from '@angular/core';
import { Yii } from "libraries/interfaces/Yii";
import { FareOptimisationType } from "@afidium/ts-facade/model/fareOptimisationType";
import { BookingType, PriceInBookingType } from "@afidium/ts-facade";
import { FareOptimisationService } from "libraries/service/fare-optimisation/fare-optimisation.service";
import { ActivatedRoute, Router } from "@angular/router";
import { MenuService } from "libraries/component/booking-information/components/menu/menu.service";
import { ESectionId } from "libraries/component/booking-information/components/menu/menu.component";
import { Location } from "@angular/common";
import { IFormattedFareOptimisations } from 'libraries/interfaces/fare-optimisation/IFareOptimisation';
import { SectorInBookingType } from "@afidium/ts-facade/model/sectorInBookingType";
import { PricesTableService } from "libraries/component/booking-information/components/booking-price-card/services/prices-table.service";
import {
    FareOptimisationNavigationService
} from "libraries/component/booking-information/components/booking-fare-optimisation-card/components/fare-optimisation-navigation/fare-optimisation-navigation.service";

class Wording {
    public readonly title: string = Yii.t('wui-sasha-js', 'Optimisation tarifaire :');
    public readonly saving: string = Yii.t('wui-sasha-js', 'Économies potentielles :');
    public readonly tarification: string = Yii.t('wui-sasha-js', 'Itinéraire');
}


@Component({
    selector: 'fare-optimisation-summary',
    template: `
        <div class="optimisation-summary-container" *ngIf="formattedData">
            <div class="optimisation-summary-offers">
                <span class="saving" (click)="goToAnchor('fareOptimisation')">{{wording.title}}</span>
                <div class="optimisation-summary-offer saving" (click)="navigateToOptimisation(formattedFareOptimisation.offerDbId)"
                     *ngFor="let formattedFareOptimisation of formattedData.fareOptimisations">
                    <a *ngIf="!isResultPage" class="savingLink">
                        {{wording.tarification}}
                        {{formattedFareOptimisation.price.cost.index}}
                        <ng-container *ngIf="formattedFareOptimisation.sectors?.length">
                            -
                            <sectors-air-simple-list
                                    class="sectors"
                                    [sectors]="formattedFareOptimisation.sectors"></sectors-air-simple-list>
                        </ng-container>
                        -
                        {{formattedFareOptimisation.offer.actionForFareOptimisation}} :
                        <span class="bold">{{formattedFareOptimisation.total?.amount | currency: formattedFareOptimisation.total?.currencyCode}}</span>
                    </a>
                    <a *ngIf="isResultPage"
                       [routerLink]="['/sasha/booking/'+syscode+'/' + formattedFareOptimisation.offerDbId]" class="savingLink">
                        {{wording.tarification}}
                        <ng-container *ngFor="let price of formattedFareOptimisation.prices; let f = first;">
                            <span *ngIf="!f; else firstElement"> / </span>
                            <ng-template #firstElement> -</ng-template>
                            {{price}}
                        </ng-container>
                        <ng-container *ngIf="formattedFareOptimisation.sectors?.length">
                            -
                            <sectors-air-simple-list
                                    class="sectors"
                                    [sectors]="formattedFareOptimisation.sectors"></sectors-air-simple-list>
                        </ng-container>
                        -
                        {{formattedFareOptimisation.offer.actionForFareOptimisation}} :
                        <span class="bold">{{formattedFareOptimisation.total?.amount | currency: formattedFareOptimisation.total?.currencyCode}}</span>
                    </a>
                </div>
            </div>
            <div>
                <span class="saving" (click)="goToAnchor('fareOptimisation')">{{wording.saving}}</span>
                <div class="optimisation-summary-saving bold">
                    {{formattedData.potentialSaving.amount | currency: formattedData.potentialSaving.currencyCode}}
                </div>
            </div>
        </div>

    `,
    styles: [`
        .optimisation-summary-container {
            align-items: center;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            margin-bottom: 5px;
            margin-top: 5px;
        }

        .optimisation-summary-offer {
            border: 1px solid lightgrey;
            background-color: white;
            display: inline-block;
            margin-right: 10px;
            margin-bottom: 5px;
            margin-top: 5px;
            padding: 7px 20px;
        }

        .optimisation-summary-offers {
            flex: 1;
        }

        .optimisation-summary-offer > a {
            color: #337ab7 !important;
        }

        .bold {
            font-weight: bold;
        }

        .optimisation-summary-saving {
            font-size: 1.2rem;
            text-align: right;
            display: inline-block;
            width: 110px;
        }

        .saving:hover {
            cursor: pointer;
        }

        .savingLink {
            border-bottom: 1px solid transparent;
        }
        
        .saving:hover > .savingLink {
            border-color: #337ab7;
        }

        .savingLink {
            text-decoration: none;
        }

        .sectors {
            display: inline-block;
        }
        
    `
    ]
})
export class FareOptimisationSummaryComponent implements OnInit {

    @Input()
    public bookingSyscode: string;

    @Input()
    public booking: BookingType = null;

    @Input()
    public syscode: string;

    @Input()
    public fareOptimisation: FareOptimisationType;

    @Input()
    public prices: PriceInBookingType[];

    public formattedData: IFormattedFareOptimisations;

    public currentUrl: string = this.router.url;

    public isResultPage: boolean = this.currentUrl.includes('booking/search-result');

    public wording: Wording = new Wording();

    public sectors: SectorInBookingType[];

    constructor(
        private readonly fareOptimisationService: FareOptimisationService,
        readonly menuService: MenuService,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly location: Location,
        private readonly fareOptimisationNavigationService: FareOptimisationNavigationService,
        private readonly pricesTablesService: PricesTableService,
    ) {
    }

    public ngOnInit(): void {
        if (this.fareOptimisation && this.booking?.priceArray?.length) {
            this.formattedData = this.fareOptimisationService.formatFareOptimisations(this.fareOptimisation, this.prices, this.booking);
        }
    }

    public goToAnchor(section: string): void {
        this.menuService.goToAnchorSubject.next(section);
    }

    public navigateToOptimisation(offerId: string): void {
        const currentUrl: string = this.router.url;

        if (!currentUrl.includes(`${offerId}`)) {

            setTimeout((): void => {
                const syscode: string = this.route.snapshot.params.syscode;
                const newUrlBegin: string = currentUrl.split(syscode)[0];
                const newUrl: string = `${newUrlBegin}${syscode}/${offerId}#${ESectionId.fareOptimisation}`;
                this.location.go(newUrl);
                this.fareOptimisationNavigationService.navigationEvent.emit(offerId);
            }, 20);
        }
        this.goToAnchor('fareOptimisation');

    }
}
